import React, { useRef, useEffect, useState } from 'react';
import { TokenInfo } from './Chat';

interface RichTextEditorProps {
  initialText: string;
  tokens: TokenInfo[];
  onTextChange: (text: string) => void;
  disabled: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialText,
  tokens,
  onTextChange,
  disabled,
}) => {
  const [isEditing, setIsEditing] = useState(true);
  const [cursorPosition, setCursorPosition] = useState(0);
  const editableRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Switch between editing and display mode
  useEffect(() => {
    setIsEditing(tokens.length === 0);
  }, [tokens]);

  // Handle text changes in edit mode
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onTextChange(e.target.value);
    setCursorPosition(e.target.selectionStart);
  };

  // Enable editing mode
  const enableEditing = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  return (
    <div className="rich-text-editor">
      {isEditing || tokens.length === 0 ? (
        <textarea
          ref={textareaRef}
          className="editor-textarea"
          value={initialText}
          onChange={handleTextareaChange}
          placeholder="Type your prompt here..."
          disabled={disabled}
          rows={8}
          aria-label="Text input for prompt"
        />
      ) : (
        <div
          ref={editableRef}
          className="editor-display"
          onClick={enableEditing}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              enableEditing();
            }
          }}
          aria-label="Generated text with token highlights. Click to edit."
        >
          <span className="prompt-text">{initialText}</span>
          <TokenHighlights tokens={tokens} />
        </div>
      )}
      {!isEditing && tokens.length > 0 && (
        <button
          className="edit-button btn btn-sm btn-outline-secondary"
          onClick={enableEditing}
          aria-label="Edit text"
        >
          ✏️ Edit
        </button>
      )}
      {isEditing && tokens.length > 0 && (
        <button
          className="edit-button btn btn-sm btn-outline-secondary"
          onClick={() => setIsEditing(false)}
          aria-label="Cancel editing"
        >
          ✖️ Cancel
        </button>
      )}
    </div>
  );
};

// Separate component for token highlights with improved tooltip behavior
const TokenHighlights: React.FC<{ tokens: TokenInfo[] }> = ({ tokens }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const getColorForProbability = (probability: number): {
    bg: string;
    text: string;
    border: string;
  } => {
    if (probability > 0.8) {
      return {
        bg: '#d4edda',
        text: '#155724',
        border: '#c3e6cb'
      };
    } else if (probability > 0.6) {
      return {
        bg: '#d1ecf1',
        text: '#0c5460',
        border: '#bee5eb'
      };
    } else if (probability > 0.4) {
      return {
        bg: '#fff3cd',
        text: '#856404',
        border: '#ffeaa7'
      };
    } else if (probability > 0.2) {
      return {
        bg: '#ffe5d0',
        text: '#8b4513',
        border: '#ffd8b8'
      };
    } else {
      return {
        bg: '#f8d7da',
        text: '#721c24',
        border: '#f5c6cb'
      };
    }
  };

  const handleMouseEnter = (index: number, event: React.MouseEvent) => {
    // Clear any pending hide timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = rect.left + rect.width / 2;
    let y = rect.bottom + 5;

    // Adjust if tooltip would go off-screen horizontally
    const tooltipWidth = 400; // approximate max width
    if (x + tooltipWidth / 2 > viewportWidth) {
      x = viewportWidth - tooltipWidth / 2 - 20;
    } else if (x - tooltipWidth / 2 < 0) {
      x = tooltipWidth / 2 + 20;
    }

    // Adjust if tooltip would go off-screen vertically
    const tooltipHeight = 400; // approximate max height
    if (y + tooltipHeight > viewportHeight) {
      y = rect.top - tooltipHeight - 5;
    }

    setTooltipPosition({ x, y });
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    // Delay hiding to allow mouse to move to tooltip
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setHoveredIndex(null);
      }
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    // Clear hide timeout when mouse enters tooltip
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsTooltipHovered(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    setHoveredIndex(null);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {tokens.map((tokenInfo, index) => {
        const colors = getColorForProbability(tokenInfo.probability);
        return (
          <>
            <span
              key={index}
              className="token-highlight"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
                borderColor: colors.border
              }}
              onMouseEnter={(e) => handleMouseEnter(index, e)}
              onMouseLeave={handleMouseLeave}
              role="button"
              tabIndex={0}
              aria-label={`Token: ${tokenInfo.token}, probability: ${(tokenInfo.probability * 100).toFixed(1)}%`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleMouseEnter(index, e as any);
                }
              }}
            >
              {tokenInfo.token}
            </span>
            {tokenInfo.token.includes('\n') && <><br /><br /></>}
          </>
        );
      })}

      {hoveredIndex !== null && (
        <TokenTooltip
          ref={tooltipRef}
          token={tokens[hoveredIndex]}
          position={tooltipPosition}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </>
  );
};

// Enhanced tooltip component with forwardRef for proper event handling
const TokenTooltip = React.forwardRef<
  HTMLDivElement,
  {
    token: TokenInfo;
    position: { x: number; y: number };
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  }
>(({ token, position, onMouseEnter, onMouseLeave }, ref) => {
  return (
    <div
      ref={ref}
      className="token-tooltip"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      role="tooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="tooltip-content">
        <div className="tooltip-header">
          <div className="selected-token">
            <strong>Selected Token:</strong>
            <code className="token-code">{token.token}</code>
          </div>
          <div className="probability-badge">
            <span className="badge bg-primary">
              {(token.probability * 100).toFixed(2)}%
            </span>
          </div>
        </div>

        <hr className="tooltip-divider" />

        <div className="tooltip-alternatives">
          <strong>Alternative Tokens:</strong>
          <div className="alternatives-list">
            {token.topAlternatives.length > 0 ? (
              token.topAlternatives.map((alt, i) => {
                const isHighProbability = alt.probability > 0.1;
                return (
                  <div
                    key={i}
                    className={`alternative-item ${isHighProbability ? 'high-prob' : ''}`}
                  >
                    <span className="alt-rank badge bg-secondary">{i + 1}</span>
                    <code className="alt-token">{alt.token}</code>
                    <div className="alt-probability-container">
                      <div
                        className="alt-probability-bar"
                        style={{ width: `${Math.max(alt.probability * 100, 2)}%` }}
                        role="progressbar"
                        aria-valuenow={alt.probability * 100}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                      <span className="alt-probability-text">
                        {(alt.probability * 100).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-alternatives">No alternatives available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

TokenTooltip.displayName = 'TokenTooltip';

export default RichTextEditor;